// @ts-nocheck
import { useEffect, useRef } from "react";
import {
  parsePath,
  patchLatestApp,
  patchApplicationById,
  interviewEligibleForJob,
  interviewStartSlotStatus,
  getLatestAppForJob,
  fmtDateTime,
  postInterviewAbandonWithFallback,
  resolveApplicationInterviewLanguage,
  INTERVIEW_START_GRACE_MINUTES,
  INTERVIEW_SLOT_CLOSED_MESSAGE,
} from "@/legacy/helpersModule";
import { Intro } from "@/features/interview/components/Intro";
import { Interview } from "@/features/interview/components/Interview";
import { Done } from "@/features/interview/components/Done";
import { enterFullscreen } from "@/shared/lib/fullscreen";
import { useAppState } from "@/app/state/AppStateProvider";
import { useToast } from "@/shared/components/ui";
import {
  sendIntroInterviewEmail,
  sendScheduledInterviewEmail,
  sendCompletionInterviewEmail,
} from "@/shared/api/meApi";

export function InterviewFlowPage() {
  const {
    active,
    role,
    jobs,
    selJob,
    jd,
    interviewPhase,
    setInterviewPhase,
    upd,
    logout,
    navigate,
    syncStateFromServer,
    setScheduleBoardFlash,
    resolvedApplicationId,
    persistCandidateNow,
    patchCandidateForHr,
  } = useAppState();
  const toast = useToast();

  useEffect(() => {
    if (!interviewPhase) setInterviewPhase("intro");
  }, [interviewPhase, setInterviewPhase]);

  const introEmailSentRef = useRef(false);
  useEffect(() => {
    if (interviewPhase !== "intro" || !active || role === "hr") return;
    const jid = selJob?.id || active?.jobId;
    if (!jid || introEmailSentRef.current) return;
    introEmailSentRef.current = true;
    void sendIntroInterviewEmail({
      jobId: jid,
      applicationId: resolvedApplicationId ?? undefined,
    }).catch((err) => {
      console.error("Intro interview email failed:", err);
      introEmailSentRef.current = false;
    });
  }, [
    interviewPhase,
    active,
    role,
    selJob?.id,
    active?.jobId,
    resolvedApplicationId,
  ]);

  if (!active) return null;

  if (interviewPhase === "done") {
    return (
      <Done
        isHR={role === "hr"}
        onDash={() => {
          setInterviewPhase(null);
          if (role === "hr") navigate("/hr");
          else {
            const rt = parsePath(window.location.pathname, window.location.search);
            if (rt.name === "apply") navigate("/");
            else navigate("/portal");
          }
        }}
      />
    );
  }

  if (interviewPhase === "interview") {
    const interviewApp =
      resolvedApplicationId != null
        ? (active.applicationHistory || []).find(
            (a) => a.applicationId === resolvedApplicationId,
          )
        : getLatestAppForJob(active.applicationHistory, selJob?.id || active?.jobId);
    return (
      <Interview
        context={{
          jd,
          candidateName: active.name,
          language: resolveApplicationInterviewLanguage(interviewApp, active),
          // Accepted once per role: a reattempt reuses this application row, so
          // the candidate is not asked again for a role they already accepted.
          rulesAcknowledged: Boolean(interviewApp?.interviewRulesAck),
        }}
        applicationId={resolvedApplicationId}
        onAbandon={async (detail) => {
          const aid = resolvedApplicationId;
          if (aid) await postInterviewAbandonWithFallback(aid, detail);
          try {
            await syncStateFromServer();
          } catch (_) {}
          setInterviewPhase(null);
          navigate(role === "hr" ? "/hr" : "/portal");
        }}
        onEnd={async (t) => {
          if (!t || !t.length) {
            try {
              await syncStateFromServer();
            } catch (_) {}
            setInterviewPhase(null);
            navigate(role === "hr" ? "/hr" : "/portal");
            return;
          }
          const completedAt = new Date().toISOString();
          const jid = selJob?.id || active?.jobId;
          const appId = resolvedApplicationId;
          let nextHist = active.applicationHistory || [];
          const appPatch = {
            interviewCompletedAt: completedAt,
            transcript: t,
            interviewCompletionStatus: "completed",
            reattemptRequestStatus: "none",
          };
          if (appId != null) {
            nextHist = patchApplicationById(nextHist, appId, appPatch);
          } else if (jid) {
            nextHist = patchLatestApp(nextHist, jid, appPatch);
          }
          const merged = {
            ...active,
            transcript: t,
            status: "INTERVIEWED",
            applicationHistory: nextHist,
          };
          upd({
            transcript: t,
            status: "INTERVIEWED",
            applicationHistory: nextHist,
          });
          try {
            if (role === "hr") {
              await patchCandidateForHr(active.id, merged);
            } else {
              await persistCandidateNow(merged);
              if (jid) {
                void sendCompletionInterviewEmail({
                  jobId: jid,
                  applicationId: appId ?? undefined,
                }).catch((err) => {
                  console.error("Completion interview email failed:", err);
                });
              }
            }
          } catch (err) {
            console.error("Interview save failed:", err);
          }
          /* Pull the recomputed status (cooling/locked/Under Review) so the
           * portal and job board update without a manual page reload. */
          try {
            await syncStateFromServer();
          } catch (_) {}
          setInterviewPhase("done");
        }}
      />
    );
  }

  return (
    <Intro
      candidate={active}
      job={selJob || jobs.find((j) => j.id === active?.jobId)}
      bypassSchedule={role === "hr"}
      eligibilityBlock={
        role === "hr"
          ? null
          : (() => {
              const j = selJob || jobs.find((x) => x.id === active?.jobId);
              if (!j?.id) return null;
              const e = interviewEligibleForJob(active, j.id);
              return e.ok ? null : e.reason;
            })()
      }
      interviewScheduledAt={
        getLatestAppForJob(active.applicationHistory, selJob?.id || active?.jobId)
          ?.interviewScheduledAt
      }
      onSchedule={async (iso) => {
        const jid = selJob?.id || active?.jobId;
        if (!jid) return;
        const prev = getLatestAppForJob(active.applicationHistory, jid)?.interviewScheduledAt;
        const appId = resolvedApplicationId;
        const schedulePatch = { interviewScheduledAt: iso };
        let nextHist = active.applicationHistory || [];
        if (appId != null) {
          nextHist = patchApplicationById(nextHist, appId, schedulePatch);
        } else {
          nextHist = patchLatestApp(nextHist, jid, schedulePatch);
        }
        const merged = { ...active, applicationHistory: nextHist };
        upd({ applicationHistory: nextHist });
        try {
          if (role === "hr") {
            await patchCandidateForHr(active.id, merged);
          } else {
            await persistCandidateNow(merged);
          }
        } catch (err) {
          console.error("Schedule save failed:", err);
          toast.show({
            tone: "danger",
            title: "Schedule not saved",
            message: "Could not save your schedule. Please try again.",
            durationMs: 6000,
          });
          return;
        }
        const wasReschedule = !!(prev && String(prev) !== String(iso));
        setScheduleBoardFlash({ jobId: jid, at: iso, rescheduled: wasReschedule });
        void sendScheduledInterviewEmail({
          jobId: jid,
          scheduledAt: iso,
          applicationId: appId ?? undefined,
        }).catch((err) => {
          console.error("Scheduled interview email failed:", err);
        });
        if (role !== "hr") {
          try {
            await syncStateFromServer();
          } catch (_) {}
          setInterviewPhase(null);
          navigate("/");
        }
      }}
      onLogout={logout}
      onStart={(lang) => {
        const j = selJob || jobs.find((x) => x.id === active?.jobId);
        if (j?.id && role !== "hr") {
          const e = interviewEligibleForJob(active, j.id);
          if (!e.ok) {
            toast.show({
              tone: "warning",
              title: "Interview cannot start",
              message: e.reason || "Interview cannot start.",
              durationMs: 6000,
            });
            return;
          }
          const sched = getLatestAppForJob(active.applicationHistory, j.id)?.interviewScheduledAt;
          const slot = interviewStartSlotStatus(sched, false);
          if (slot.blocked) {
            toast.show({
              tone: "warning",
              title: slot.tooEarly ? "Too early to start" : "Interview window closed",
              message: slot.tooEarly
                ? `Start unlocks at ${fmtDateTime(sched)}. You have ${INTERVIEW_START_GRACE_MINUTES} minutes after that to begin.`
                : INTERVIEW_SLOT_CLOSED_MESSAGE,
              durationMs: 7000,
            });
            return;
          }
        }
        const appId = resolvedApplicationId;
        if (appId != null) {
          const nextHist = patchApplicationById(active.applicationHistory || [], appId, {
            interviewLanguage: lang,
          });
          upd({ lang, applicationHistory: nextHist });
        } else {
          upd({ lang });
        }
        // Enter full-screen "kiosk" mode as part of this click gesture (browsers
        // only allow fullscreen from a user gesture). Covers every job type
        // (scripted or AI-only) since the Intro always precedes the interview.
        void enterFullscreen();
        setInterviewPhase("interview");
      }}
      onBack={() => {
        setInterviewPhase(null);
        if (role === "hr") navigate("/hr");
        else {
          const rt = parsePath(window.location.pathname, window.location.search);
          if (rt.name === "apply") navigate("/");
          else navigate("/portal");
        }
      }}
    />
  );
}
